// @ts-nocheck
import type { ComponentProps, ReactNode } from "react"
import type { TextFieldProps as AriaTextFieldProps } from "react-aria-components"
import type { FieldError } from "react-hook-form"

import { HintText } from "@/components/ui-kit/untitled/base/input/hint-text"
import {
  Input as KitInput,
  InputBase as KitInputBase,
  type InputBaseProps as KitInputBaseProps,
  TextField as KitTextField,
} from "@/components/ui-kit/untitled/base/input/input"

import { cx } from "@uui/utils/cx"

/*
 * Control heights come from the digest — 28 / 32 / 36 / 44 — and match the
 * buttons: an `sm` input (32) sits beside an `md` button (32); `md` (36) is a
 * form field; `lg` (44) is anything a coordinator taps on a phone. The kit only
 * knows two sizes, so `lg` borrows its `md` geometry and takes the height here.
 */
export type ControlSize = "sm" | "md" | "lg"

const KIT_SIZE: Record<ControlSize, "sm" | "md"> = {
  sm: "sm",
  md: "md",
  lg: "md",
}

const HEIGHT: Record<ControlSize, string> = {
  sm: "h-8 py-0 text-sm",
  md: "h-9 py-0 text-sm",
  lg: "h-11 py-0 text-md",
}

type KitInputProps = ComponentProps<typeof KitInput>

type WithSize<T> = Omit<T, "size"> & { size?: ControlSize }

export type InputBaseProps = WithSize<KitInputBaseProps>
export type InputProps = WithSize<KitInputProps>

export function InputBase({
  size = "sm",
  inputClassName,
  ...props
}: InputBaseProps) {
  return (
    <KitInputBase
      size={KIT_SIZE[size]}
      inputClassName={cx(HEIGHT[size], inputClassName)}
      {...props}
    />
  )
}

export function Input({ size = "sm", inputClassName, ...props }: InputProps) {
  return (
    <KitInput
      size={KIT_SIZE[size]}
      inputClassName={cx(HEIGHT[size], inputClassName)}
      {...props}
    />
  )
}

export { HintText }
export { InputGroup } from "@/components/ui-kit/untitled/base/input/input-group"
export { Label } from "@/components/ui-kit/untitled/base/input/label"
export { FirstNameInput } from "@/components/ui/input-first-name"
export { LastNameInput } from "@/components/ui/input-last-name"
export { EmailInput } from "@/components/ui/input-email"
export { PhoneInput } from "@/components/ui/input-phone"

interface TextFieldProps
  extends
    AriaTextFieldProps,
    WithSize<
      Pick<
        KitInputBaseProps,
        | "size"
        | "wrapperClassName"
        | "inputClassName"
        | "iconClassName"
        | "tooltipClassName"
      >
    > {
  error?: FieldError
  children?: ReactNode
}

/*
 * A TextField carries its size to the InputBase inside it through the kit's
 * context, so the height lands whether the page sizes the field or the input.
 */
export function TextField({
  error,
  size,
  inputClassName,
  children,
  ...props
}: TextFieldProps) {
  return (
    <KitTextField
      {...props}
      size={size ? KIT_SIZE[size] : undefined}
      inputClassName={size ? cx(HEIGHT[size], inputClassName) : inputClassName}
    >
      {children}
      {error && <HintText isInvalid>{error.message}</HintText>}
    </KitTextField>
  )
}
